import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from '../../../../../models/chart';
import { ToastContext, Icon, SplitContainer, Message, FrameCard, Button, InputLabel, Input, TranslateDropdown } from '@weezevent/nacre';
import { useForm } from '../../../../../utils/validators';
import LoadingCard from '../../../../../components/loadingCard';
import Style from './translation.module.css';
import { LANGUAGES } from '../../../../../components/translations/translationModal';

function FormHeader({ form, onEdit = () => {}, languageList, language, setLanguage = () => {} }) {
    const { t } = useTranslation();

    const defaultLangs = languageList.map(lang => lang.value);

    // selected is array of all the languages that have a translation value
    let selected = React.useMemo(() => {
        let _selected = [];
        Object.values(form).forEach(value => {
            Object.entries(value).forEach(([k, v]) => {
                // if the language is in translation but not in default language list we don't push it in selected
                if (v && defaultLangs.includes(k) && !_selected.includes(k)) {
                    _selected.push(k);
                }
            });
        });
        return _selected;
    }, [defaultLangs, form]);

    return (
        <div className={Style['header']}>
            <div className={Style['title-container']}>
                <div className={Style['title']}>{t('ticket.charts.translation.title')}</div>
                <div className={Style['subtitle']}>{t('ticket.charts.translation.subtitle')}</div>
            </div>
            <div className={Style['title-right']}>
                <div className={Style['edit']} onClick={onEdit}>
                    <div className={Style['edit-label']}>{t('ticket.common.action.edit')}</div>
                    <Icon name="edit" className={Style['edit-icon']} />
                </div>
                <div className={Style['translate-dropdown']}>
                    <TranslateDropdown defaultValue={language} languages={languageList} onChange={e => setLanguage(e)} selected={selected} />
                </div>
            </div>
        </div>
    );
}

export default function TranslationContainer({ organization, chartId, loadingZones }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const languageList = React.useMemo(() => {
        return organization.languages.map(lang => {
            return {
                label: LANGUAGES[lang.toLowerCase()],
                value: lang
            };
        });
    }, [organization]);

    const defaultLanguage = React.useMemo(() => {
        // default language is default_locale from organization. If organization default language
        // is not in the default language list than we return english
        const languageOrga = organization.default_locale.replace('_', '-').split('-')[0];
        // Sometimes we can have lowercase 'fr-fr' in languageList but uppercase in orga.default FR_fr
        // So we compare after cast in lowercase
        const localeDefaultOrga = languageList.find(lang => lang.value.split('-')[0].toLowerCase() === languageOrga.toLowerCase());
        return localeDefaultOrga.value || 'en-gb';
    }, [organization]);

    const [edit, setEdit] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [language, setLanguage] = React.useState(defaultLanguage);

    const defaultForm = {
        stage_name: {}
    };

    const [form, setForm, setFormObject] = useForm(defaultForm);

    // we create a object of languages that are in language list, for the moment all with empty string value
    const defaultFormLanguages = React.useMemo(() => {
        let _formLanguages = {};
        languageList.forEach(language => {
            _formLanguages[language.value] = '';
        });
        return _formLanguages;
    }, []);

    // here we merge our defaultFormLanguages with languages that are in chart.translations
    const loadForm = React.useCallback(
        chartTranslation => {
            const _form = Object.entries(chartTranslation).reduce((acc, [k, v]) => {
                acc[k] = v ? { ...defaultFormLanguages, ...v } : { ...defaultFormLanguages };
                return acc;
            }, {});
            return { ...defaultForm, ..._form };
        },
        [defaultFormLanguages]
    );

    const [chart, loadingChart, syncChart] = Chart.useApiModel(
        {
            id: chartId,
            organizationId: organization.id,
            onSuccess: chart => {
                setFormObject(loadForm(chart.translations));
            }
        },
        [chartId, organization, loadForm, setFormObject]
    );

    const handleSubmit = React.useCallback(() => {
        setIsSubmitting(true);
        let new_chart = new Chart(chart);
        // TODO send empty values - Pyvar doesnt accept empty values
        // so we delete the key if it is with empty value
        let _form = { ...form };
        Object.entries(_form).forEach(([k, v]) => {
            Object.entries(v).forEach(([key, value]) => {
                if (!value) {
                    delete _form[k][key];
                }
            });
        });
        new_chart.translations = _form;
        new_chart
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.charts.translation.toast-success'));
                syncChart();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.charts.translation.toast-error'));
            })
            .finally(() => {
                setIsSubmitting(false);
                setEdit(false);
            });
    }, [chart, form, organization, toast, syncChart]);

    const onChange = React.useCallback(
        (value, language, key) => {
            let _formItem = { ...form[key] };
            _formItem[language] = value;
            setForm(key, _formItem);
        },
        [form, setForm]
    );

    if (loadingChart) {
        return null;
    }

    return (
        <SplitContainer
            additional={
                <Message type="tips" title={t('notifications.tips_title')} mini>
                    <p>{t('ticket.charts.translation.tips')}</p>
                </Message>
            }
        >
            <FrameCard title={<FormHeader form={form} onEdit={() => setEdit(true)} language={language} setLanguage={setLanguage} languageList={languageList} />}>
                <LoadingCard loading={loadingZones} height={'200px'}>
                    {!edit ? (
                        <table className={Style['table-form-answers']}>
                            <tbody>
                                {Object.entries(form).map(([key, value]) => (
                                    <tr key={key}>
                                        <td className={Style['td-field']}>{t(`ticket.charts.translation.${key}`)}</td>
                                        <td className={Style['td-answer']}>{(value && value[language]) || chart[key] || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <>
                            {Object.entries(form).map(([key, value]) => (
                                <div className={Style['field']} key={key}>
                                    <InputLabel label={t(`ticket.charts.translation.${key}`)} />
                                    <Input value={value[language]} onChange={({ target: { value } }) => onChange(value, language, key)} />
                                </div>
                            ))}
                            <Button.Group className={Style['button-group-edit']}>
                                <Button primary isLoading={isSubmitting} disabled={isSubmitting} onClick={handleSubmit} label={t('ticket.common.action.save')} />
                                <Button
                                    onClick={() => {
                                        setEdit(false);
                                        setFormObject(loadForm(chart.translations));
                                    }}
                                    label={t('ticket.common.action.cancel')}
                                />
                            </Button.Group>
                        </>
                    )}
                </LoadingCard>
            </FrameCard>
        </SplitContainer>
    );
}
